import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSocket } from '../../contexts/SocketContext';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  LayoutDashboard,
  Building2,
  Users,
  MapPin,
  Grid3X3,
  Layers,
  Radio,
  Bell,
  Map,
  Settings,
  LogOut,
  Sun,
  Moon,
  Wifi,
  WifiOff,
  Sprout,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { connected } = useSocket();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    ...(isSuperAdmin ? [{ to: '/organizations', icon: Building2, label: 'Organisations' }] : []),
    ...(isAdmin ? [{ to: '/users', icon: Users, label: 'Utilisateurs' }] : []),
    { to: '/farms', icon: MapPin, label: 'Exploitations' },
    { to: '/map', icon: Map, label: 'Carte' },
    { to: '/parcels', icon: Grid3X3, label: 'Parcelles' },
    { to: '/zones', icon: Layers, label: 'Zones' },
    { to: '/sensors', icon: Radio, label: 'Capteurs' },
    { to: '/alerts', icon: Bell, label: 'Alertes' },
  ];

  const roleLabels = {
    super_admin: 'Super Admin',
    org_admin: 'Admin Organisation',
    viewer: 'Observateur',
  };

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r bg-slate-900 text-slate-50" data-testid="sidebar">
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-slate-800 px-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
          <Sprout className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-lg font-semibold tracking-tight font-['Outfit']">AgriTech Pro</h1>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="h-[calc(100vh-16rem)]">
        <nav className="space-y-1 p-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.slice(1)}`}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-800 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-slate-800 bg-slate-900 p-4">
        {/* Connection Status */}
        <div className="mb-3 flex items-center gap-2 text-xs">
          {connected ? (
            <>
              <Wifi className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-slate-400">Temps réel actif</span>
            </>
          ) : (
            <>
              <WifiOff className="h-3.5 w-3.5 text-red-500" />
              <span className="text-slate-400">Hors ligne</span>
            </>
          )}
        </div>

        {/* Theme Toggle */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            {theme === 'light' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{theme === 'light' ? 'Mode clair' : 'Mode sombre'}</span>
          </div>
          <Switch
            checked={theme === 'dark'}
            onCheckedChange={toggleTheme}
            data-testid="theme-toggle"
          />
        </div>

        <Separator className="my-3 bg-slate-800" />

        {/* User Info */}
        <div className="mb-3">
          <p className="text-sm font-medium text-white">
            {user?.first_name} {user?.last_name}
          </p>
          <p className="text-xs text-slate-400">{roleLabels[user?.role] || user?.role}</p>
        </div>

        {/* Logout */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full justify-start text-slate-400 hover:bg-slate-800 hover:text-white"
          data-testid="logout-btn"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
